/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import AnalyticsUtil from './AnalyticsUtil';
import GMOData from './GMOMarketing';

export default class DiscoverAnalytics {
	ingest: ingestObject;
	analyticsUtils: AnalyticsUtil;

	constructor() {
		this.analyticsUtils = new AnalyticsUtil();
		this.ingest = this.analyticsUtils.ingest;
	}

	/**
	 * [sendToIngest Post to ingest]
	 * @returns {[none]}
	 */
	sendToIngest() {
		this.analyticsUtils.checkOptin(this.ingest);
	}

	/**
	 * [initAnalytics Init Ingest]
	 * @returns {[none]}
	 */
	initAnalytics() {
		this.ingest.initializeDefaultData();
	}

	/**
	 * [logAnalytics A utility method to set multiple values at once]
	 * @param  {[object of key/value pairs]} dataObject [events]
	 * @returns {[none]}
	 */
	logAnalytics(dataObject: dataObject) {
		if (
			window
			&& window.cchome
			&& window.cchome.util
			&& window.cchome.util.getStartupLanguage() === 'en'
			&& window.cchome
			&& window.cchome.util
			&& window.cchome.util.hasFeature
			&& window.cchome.util.hasFeature('uk')
		) {
			dataObject.setEventLanguage = 'en_UK';
		}

		this.analyticsUtils.logAnalytics(dataObject);
	}

	/**
	 * Checks if event is relevant to GMO, if it is then sends
	 * to GMO.
	 * @param {Object} data
	 */
	gmoChecks(data: dataObject) {
		// Currently not using but may want later
		const discoverRenderPageEvent = {
			setEventSubtype: 'page',
			setEventType: 'render',
			setEventWorkFlow: 'Discover',
		};

		// light subset equality for objects with values as strings
		// checks if one object is the subset of another
		const isSubset = (supersetObject, subsetObject) => {
			const missingKeys = Object.keys(subsetObject).filter(key => subsetObject[key] !== supersetObject[key]);
			return missingKeys.length === 0;
		};

		if (isSubset(data, discoverRenderPageEvent)) {
			this.sendGMOData('render');
		}
		else if (data.setEventType === 'click') {
			this.sendGMOData('click');
		}
	}

	/**
	 * Send data to GMO team.
	 * @param {string} action  - ex. render, click, etc
	 */
	sendGMOData(action: string) {
		const gmoData = new GMOData();
		gmoData.dunamisToGMOData(this.ingest.metricsCollection, action);
	}

	// Commenting the code since this is old and perhaps not needed anymore.
	// TODO: Delete the below commented code if analytics doesn't need it.
	/**
	 * Logs article analytics to Console log. Request by Haresh
	 * // TODO: Remove after debugging with him.
	 * @param {Object} data
	 */
	// logArticleAnalytics(data: dataObject) {
	// 	if (data.setEventSubcategory.toLowerCase() === 'article') {
	// 		console.log('article iframed event', this.ingest.metricsCollection);
	// 	}
	// }

	/**
	 * Send the custom object to ingest.
	 *
	 * If data is undefined, do nothing.
	 * If this.ingest is undefined, that likely means the browser is not defined because it's still loading.
	 * In that case, don't send any analytics.
	 *
	 * Returns a promise.
	 * - Use the promise to know when the analytic call has been processed
	 * It doesn't say if the request was successful or not.
	 *
	 * @param {object || undefined} data
	 * @returns {Promise[none]}
	 */
	postCustomData(data: Object) {
		if (data && this.ingest) {
			this.initAnalytics();
			this.logAnalytics(data);
			const result = this.analyticsUtils.sendToIngest(this.ingest, true);

			this.gmoChecks(data);

			// TODO: remove later after debug
			// this.logArticleAnalytics(data);
			return result;
		}
		else if (data) {
			console.error('Could not log analytics because browser did not render');
		}
	}
}
